import { useState, useRef, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  multiline?: boolean;
  rows?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

export default function VoiceInput({ value, onChange, placeholder, className, style, multiline, rows, onKeyDown }: Props) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const toggleListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let finalTranscript = value;

    recognition.onresult = (event: any) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? ' ' : '') + transcript;
          onChange(finalTranscript);
        } else {
          interim = transcript;
        }
      }
      // Show interim results appended to current value
      if (interim) {
        onChange(finalTranscript + (finalTranscript ? ' ' : '') + interim);
      }
    };

    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [listening, value, onChange]);

  const inputProps = {
    value,
    onChange: (e: any) => onChange(e.target.value),
    placeholder,
    className: `${className || ''} pr-10`,
    style,
    onKeyDown,
  };

  return (
    <div className="relative">
      {multiline ? (
        <textarea {...inputProps} rows={rows || 3} />
      ) : (
        <input type="text" {...inputProps} />
      )}
      <button
        type="button"
        onClick={toggleListening}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors"
        style={{
          color: listening ? '#ef4444' : 'var(--text-muted)',
          background: listening ? 'rgba(239,68,68,0.1)' : 'transparent',
        }}
        title={listening ? 'Stop listening' : 'Voice input'}
      >
        {listening ? <MicOff size={16} /> : <Mic size={16} />}
      </button>
    </div>
  );
}
